/**
 * Pre-inline every <img> inside a DOM node as a base-64 data URL so that
 * `html-to-image` (or any canvas-based export) captures the images cleanly
 * on mobile browsers.
 *
 * Why this exists — mobile Safari and Chrome-Android intermittently drop
 * cross-origin `<img>` fetches while `html-to-image` is serializing the DOM
 * (a race between the internal `fetch()` calls and the Web Share API grab).
 * Swapping the `src`s to same-origin data URLs before capture eliminates the
 * race and the CORS-taint risk on canvas.
 *
 * Usage:
 *   const restore = await preloadImagesForCapture(node);
 *   try {
 *     const pngDataUrl = await toPng(node, {...});
 *   } finally {
 *     restore();
 *   }
 */
export async function preloadImagesForCapture(node) {
  if (!node) return () => {};
  const imgs = Array.from(node.querySelectorAll('img'));
  const originals = imgs.map((img) => img.getAttribute('src') || '');

  await Promise.all(
    imgs.map(async (img, i) => {
      const src = originals[i];
      // Skip empty srcs and already-inline images.
      if (!src || src.startsWith('data:')) return;
      try {
        const res = await fetch(src, { mode: 'cors', cache: 'reload' });
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        img.removeAttribute('crossorigin');
        img.src = dataUrl;
        // Belt-and-braces: wait for the browser to actually decode the swapped
        // src so html-to-image rasterizes the real pixels, not a placeholder.
        if (!img.complete) {
          await new Promise((resolve) => {
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          });
        }
        if (img.decode) {
          try {
            await img.decode();
          } catch (_) {
            /* ignore */
          }
        }
      } catch (_) {
        // Leave the original src alone — the image simply won't appear in
        // the export, matching the previous failure mode rather than
        // making things worse.
      }
    })
  );

  // Return a restore function so the on-screen preview keeps working with
  // the network-hosted URLs (better caching, lazy re-render on state change).
  return () => {
    imgs.forEach((img, i) => {
      if (originals[i] && img.getAttribute('src') !== originals[i]) {
        img.setAttribute('src', originals[i]);
      }
    });
  };
}
